import {
    AddressItemType,
    addUserFile,
    ipmQueryClient,
    PostageUsageType,
    sendMail,
    useInfiniteAddresses, UserFileCategory
} from "@twigsby/client-data";
import {
    Button,
    ButtonSubmit,
    Card,
    CardLayoutType,
    ColourSolid,
    FullWidthSidebar, SelectItemType,
    useLayout
} from '@twigsby/frontend-framework';
import {FieldValues, FormProvider, useForm} from "@twigsby/frontend-framework/lib/ReactHookForm";
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from "@twigsby/frontend-framework";
import {AddAddressModal} from "../../Components/AddAddress";
import {AddressControl} from "../../Components/AddressControl";
import {PostageControl} from "../../Components/PostageControl";
import {FileControl} from "../../Components/SendMail/Controls/FileControl";
import SendsNavigation from "../../Components/SendMail/Shared/Controls/SendsNavigation";
import SendMailProcessingModal from "../../Components/SendMail/Shared/Modals/SendMailProcessingModal";
import {SendDataProcess} from "../../Components/SendsAdmin/StandaloneSendForm";
import {useSite} from "../../Providers/SiteInterfaceProvider";

const SendMailPage = () => {
    const { t } = useTranslation();
    const { isMedium } = useLayout();
    const { currentLocale, updatePageMeta } = useSite();
    const sendMailRef = useRef<any>();
    const sendMailNavigationRef = useRef<any>();

    const [addAddressActive, setAddAddressActive] = useState<boolean>(false);
    const [activeAddress, setActiveAddress] = useState<AddressItemType | null>(null);
    const [activePostage, setActivePostage] = useState<SelectItemType | null>(null);

    const { isLoading, data, error } = useInfiniteAddresses();
    const [ addressOptions, setAddressOptions] = useState<Array<any>>([]);
    const [ defaultAddress, setDefaultAddress ] = useState<Array<number> | undefined>(undefined);

    const [ sendError, setSendError ] = useState<string | null>(null);
    const [ reset, setReset ] = useState<boolean>(false);

    const [ sendProcessData, setSendProcessData ] = useState<SendDataProcess>({
        form: null,
        address: '',
        postage: '',

        sendProcessOpen: false,

        uploadFileActive: false,
        sendDataActive: false,

        postingData: false,

        sendComplete: false
    });

    const formMethods = useForm({
        defaultValues: {
            address: -1,
            postage: -1,
            file: null,
        },
        mode: 'onChange'
    });

    const {
        handleSubmit,
        setValue,
        reset: formReset,
        formState: { isValid },
    } = formMethods;

    const FilterSelectedAddress = () => {
        let filteredAddress : Array<any> = [];

        data?.pages.forEach((page) => page?.data && page.data.forEach((address) => {
            filteredAddress.push({ 'label': address.label, 'value': address.value });
        }));

        setAddressOptions(filteredAddress);
    };

    const HandleProcessStart = (data: FieldValues) => {
        setSendError(null);
        setSendProcessData({
            ...sendProcessData,
            form: data,
            sendProcessOpen: true,
            address: activeAddress?.label,
            postage: activePostage?.label
        });
    }

    const HandleUpload = () => {
        setSendProcessData({...sendProcessData, uploadFileActive: true, postingData: true });

        addUserFile(sendProcessData.form.file[0], UserFileCategory.MailSends).then((r) => {
            setSendProcessData({...sendProcessData, uploadFileActive: false, sendDataActive: true, postingData: true });
            HandleSend(r.fileUuid);
        })
            .catch((e) => {
                setSendError(e.toString());
                setSendProcessData({...sendProcessData, sendProcessOpen: false, uploadFileActive: false, sendDataActive: false, postingData: false });
            });
    }

    const HandleSend = (fileUuid: string) => {
        sendMail({
            fileUuid: fileUuid,
            notes: sendProcessData.form.notes,
            sendsPageId: sendProcessData.form.sendsPageId,
            addressId: sendProcessData.form.address,
            postageOptionId: sendProcessData.form.postage,
        }).then((s) => {
            setSendProcessData({ ...sendProcessData, sendDataActive: false, sendComplete: true, postingData: true, mailUuid: s.mailUuid });
            ipmQueryClient.invalidateQueries(['allSendsHistory']).then(() => null);
        })
            .catch((e) => {
                setSendError(e.toString());
                setSendProcessData({...sendProcessData, sendProcessOpen: false, uploadFileActive: false, sendDataActive: false, postingData: false });
            });
    }

    const ResetSend = () => {
        formReset();
        setReset(!reset);
        setSendProcessData({
            address: '',
            postage: '',
            form: null,

            sendProcessOpen: false,

            uploadFileActive: false,
            sendDataActive: false,

            postingData: false,
            sendComplete: false
        });
    }

    useEffect(() => {
        if(data) {
            FilterSelectedAddress();
        }
    }, [data]);

    useEffect(() => {
        updatePageMeta({
            title: t('sends.title')
        });
    }, [currentLocale]);

    return (
        <FullWidthSidebar
            ref={sendMailRef}
            navigationComponent={
                <SendsNavigation
                    ref={sendMailNavigationRef}
                    title={t('sends.title')}
                />
            }
            extraContentClass={`flex-column send-mail-container`}
        >
            <div className={'column-adjuster pl-md pr-md'}>
                <div className={'container-narrow'}>
                    <div className='row no-gap'>
                        <div className='col-xs-12 col-lg-3 col-xl-3'/>

                        <div className='col-xs-12 col-lg-9 col-xl-6'>
                            <Card headerOptions={{title: ''}} type={CardLayoutType.SlimHeader}>

                                <FormProvider {...formMethods}>
                                    <form onSubmit={handleSubmit((data, e) => HandleProcessStart(data))}>
                                        <fieldset>
                                            <h4 className={'mb-0'}>{t('sends.title')}</h4>
                                            <p>{t('sends.description')}</p>

                                            <hr/>

                                            <div className={'flex-column flex-gap-md'}>
                                                <AddressControl
                                                    name={'address'}
                                                    singleSelection={true}
                                                    forwardItemCallback={(e) => {
                                                        setValue('address', e.value);
                                                        setActiveAddress(e);
                                                    }}
                                                    defaultValues={defaultAddress ? defaultAddress : undefined}
                                                    showCreateAddressOptions={true}
                                                    hideImportOptions={true}
                                                    newAddressCallback={(e) => {
                                                        setValue('address', e.addressId);
                                                        setDefaultAddress([e.addressId]);
                                                        setActiveAddress({
                                                            value: e.addressId,
                                                            label: e.addressString as string,
                                                            object: e
                                                        });
                                                    }}
                                                    reset={reset}
                                                />

                                                <PostageControl
                                                    name={'postage'}
                                                    postageType={PostageUsageType.Sends}
                                                    activeAddress={activeAddress}
                                                    singleSelection={true}
                                                    forwardValueCallback={(e) => {
                                                        setValue('postage', e);
                                                    }}
                                                    forwardItemCallback={(e) => {
                                                        setValue('postage', e.value);
                                                        setActivePostage(e);
                                                    }}
                                                    reset={reset}
                                                />

                                                <FileControl
                                                    name={'file'}
                                                    reset={reset}
                                                />

                                                <ButtonSubmit title={t('sends.actions.send')}
                                                              colour={ColourSolid.Primary} fullWidth={true}
                                                              disabled={!isValid}/>
                                            </div>
                                        </fieldset>
                                    </form>
                                </FormProvider>

                            </Card>

                        </div>
                    </div>
                </div>
            </div>

            <SendMailProcessingModal
                isOpen={sendProcessData.sendProcessOpen as boolean}
                sendData={sendProcessData}
                actionedCallback={() => HandleUpload()}
                closeCallback={() => sendProcessData.postingData ? ResetSend() : setSendProcessData({
                    ...sendProcessData,
                    sendProcessOpen: false
                })}
                lockOpen={sendProcessData.postingData}
            />

            <AddAddressModal isOpen={addAddressActive} closeCallback={() => setAddAddressActive(false)}
                             actionedCallback={(id: number) => null}/>

        </FullWidthSidebar>
    );
};

export default SendMailPage;
